import aldoCardSmall from 'assets/images/aldo-card-small.png';
import classNames from 'classnames';
import { Alert } from 'components/atoms/Alert';
import { AlertV2 } from 'components/atoms/AlertV2';
import { Button } from 'components/atoms/Button';
import { Modal } from 'components/atoms/Modal';
import ShoppingCartIcon2 from 'components/atoms/SVG/ShoppingCartIcon2.component';
import { isUserConnectedToWallet } from 'components/pages/Banks/Wallet/helpers';
import ExternalTransfer from 'components/pages/Banks/Wallet/MoneyTransfer/components/ExternalTransfer.component';
import useGlobalBankingSignupModal from 'hooks/Bank/useGlobalBankingSignupModal';
import useAccountableBankingEntryPoints from 'hooks/Bank/useAccountableBankingEntryPoints';
import useGetConnectors from 'hooks/Bank/useGetConnectors';
import useBasketDraftList from 'hooks/useBasketDraftList';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { IExpenseFormValuesV2 } from 'types/expenses.types';
import { CurrenciesEnum } from 'types/global.types';
import type { Connector } from 'types/transactions.types';
import { logAmplitude } from 'utils/amplitude';
import {
  WEBAPP_BASKET_RECIPIENTDETAILSFORM_CLICKEDADDTOBASKET,
  WEBAPP_EXPENSEFORM_CLICKEDADDTOBASKET,
  WEBAPP_EXPENSE_ADDED_TO_PAYMENTBASKET,
} from 'utils/amplitude/events/expenses.amplitude';
import {
  structuredCommunicationPattern,
  structuredCommunicationPatternWithPlus,
} from 'utils/constants';
import { AccountablePawLogo } from 'utils/icons/figmaIcons';
import showToast from 'utils/toast';
import useAddExpenseToBasket from '../useAddExpenseToBasket';
import useGetBasket from '../useGetBasket';
import {
  CommunicationTypes,
  TransferFieldNames,
  TransferReasons,
  TransferTypes,
} from '../../Banks/Wallet/MoneyTransfer/types';

const AddExpenseToBasket: FC<{
  expense: IExpenseFormValuesV2;
  isAddedToBasket?: boolean;
  validateExpense: () => Promise<any>;
}> = ({ expense, isAddedToBasket, validateExpense }) => {
  const { t } = useTranslation();
  const openSignupFlow = useGlobalBankingSignupModal();

  const { basketDraftList, setBasketDraftList } = useBasketDraftList();

  const { data: connectors, isLoading: isLoadingConnectors } =
    useGetConnectors();

  const _isUserConnectedToWallet = isUserConnectedToWallet(
    connectors as Connector[],
  );

  const { shouldHideEntryPoints, hideEntryPoints, dismissDialog } =
    useAccountableBankingEntryPoints();

  const addExpenseToBasketAPI = useAddExpenseToBasket(() => {
    showToast(
      { title: t('accountable_banking.expenses_basket.success_msg') },
      {
        type: 'success',
      },
    );
  });

  const basketAPI = useGetBasket();

  if (isLoadingConnectors) return null;

  if (!_isUserConnectedToWallet) {
    if (shouldHideEntryPoints) return <>{dismissDialog}</>;

    return (
      <>
        {dismissDialog}
        <AlertV2
          type="neutre"
          variant="white"
          size="large"
          className="relative overflow-hidden"
          title={t('accountable_banking.pay_with_accountable.title')}
          description={t('accountable_banking.pay_with_accountable.desc')}
          dismissible
          onDismiss={() => hideEntryPoints('expense_form')}
          extra={
            <img
              src={aldoCardSmall}
              alt="accountable-card"
              className="w-16 absolute bottom-0 -right-1 rotate-[8deg] pointer-events-none"
            />
          }
          renderIcon={(className) => (
            <AccountablePawLogo
              className={classNames(className, 'text-[#7E4ADE]')}
            />
          )}
          actions={
            <div>
              <Button
                asDefault
                className="underline text-sm"
                tracingEvent={WEBAPP_EXPENSEFORM_CLICKEDADDTOBASKET}
                onClick={() => openSignupFlow()}
              >
                {t('learn_more')}
              </Button>
            </div>
          }
        />
      </>
    );
  }

  const expenseGuessedCommunication =
    expense?.guessed_data?.structured_communication || '';

  const isWrappedStructuredCommunication = expenseGuessedCommunication.match(
    structuredCommunicationPatternWithPlus,
  );

  const isUnwrappedStructuredCommunication = expenseGuessedCommunication.match(
    structuredCommunicationPattern,
  );

  const guessedCommunication = isUnwrappedStructuredCommunication
    ? `+++${expenseGuessedCommunication}+++`
    : expenseGuessedCommunication;

  return (
    <div>
      <Modal
        name="Add to basket - Payment details form"
        trigger={(api) =>
          isAddedToBasket ? (
            <Alert
              type="success"
              title={t(
                'accountable_banking.expenses_basket.added_to_basket.title',
              )}
            />
          ) : (
            <Button
              structure="primary"
              color="taxes"
              className="w-full mt-1 gap-1.5"
              tracingEvent={WEBAPP_EXPENSEFORM_CLICKEDADDTOBASKET}
              onClick={() => {
                validateExpense().then((valid) => {
                  if (valid) {
                    return api.openModal();
                  }

                  return showToast(
                    {
                      title: t(
                        'accountable_banking.expenses_basket.invalid_expense_error',
                      ),
                    },
                    {
                      type: 'info',
                    },
                  );
                });
              }}
            >
              <span className="text-base">
                {t('accountable_banking.expenses_basket.cta')}
              </span>
              <ShoppingCartIcon2 className="text-white w-5 h-5" />
            </Button>
          )
        }
        render={(api) => (
          <ExternalTransfer
            formTitle={t(
              'accountable_banking.expenses_basket.edit_expense.modal.title',
            )}
            formSubtitle={t(
              'accountable_banking.expenses_basket.edit_expense.modal.subtitle',
            )}
            formSubtitleClassName="w-[70%]"
            btnLabel={t(
              'accountable_banking.expenses_basket.add_expense.modal.cta',
            )}
            formType="basket"
            loading={addExpenseToBasketAPI.isLoading}
            initialData={{
              amount:
                expense?.items?.reduce(
                  (agg, current) => agg + (current.currencyAmount || 0),
                  0,
                ) || 0, // Multipy by the exchange rate when expenses currencies are live
              recipient_name: expense?.supplier?.name,
              recipient_iban: expense?.guessed_data?.supplier?.iban,
              // communication: `${expense?.supplier?.name} - ${expense.expenseDate}`,

              // TODO: check with Mohamed
              // recipient_iban: expense?.guessedIBAN,
              communication: guessedCommunication,

              communication_type:
                isWrappedStructuredCommunication ||
                isUnwrappedStructuredCommunication
                  ? CommunicationTypes.structured
                  : CommunicationTypes.unstructured,
              instant: false,
            }}
            onSubmit={(values) => {
              const body = {
                document: {
                  id: expense._id as string,
                  type: 'expense',
                },
                type: values?.[TransferFieldNames.instant]
                  ? TransferTypes.instant_sepa
                  : TransferTypes.sepa,
                amount: {
                  value: values?.[TransferFieldNames.amount] as number,
                  currency: CurrenciesEnum.EUR,
                },
                communication: {
                  message: values?.[TransferFieldNames.communication] as string,
                  type: values?.[
                    TransferFieldNames.communication_type
                  ] as CommunicationTypes,
                },
                beneficiary: {
                  name: values?.[TransferFieldNames.recipient_name] as string,
                  iban: values?.[TransferFieldNames.recipient_iban] as string,
                },
                reason: TransferReasons.expense,
                originalExpense: expense,
                isDraft: !expense._id,
                _id: !expense._id ? expense._frontendId : undefined,
              };

              if (body.isDraft) {
                setBasketDraftList(basketDraftList.concat([body]));

                basketAPI.refetch();

                logAmplitude(WEBAPP_EXPENSE_ADDED_TO_PAYMENTBASKET);

                return api.closeModal();
              }

              return addExpenseToBasketAPI.mutateAsync(body).then(() => {
                logAmplitude(WEBAPP_EXPENSE_ADDED_TO_PAYMENTBASKET);
                api.closeModal();
              });
            }}
            color="expenses"
            buttonAmplitudeEvent={
              WEBAPP_BASKET_RECIPIENTDETAILSFORM_CLICKEDADDTOBASKET
            }
          />
        )}
      />
    </div>
  );
};

export default AddExpenseToBasket;
